var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import SamplePlayer from './SamplePlayer';
import { generateNotesFromChord } from '../utils/Chords';
// Using require() to import JS in TS files
var soundApi = require('./sound');
var constants = require('../constants');
// Default to 4/4 time
var BEATS_PER_MEASURE = 4;
var DEFAULT_BPM = 120;
/**
 * Main music player component which maintains the list of playback events and
 * uses a {@link SamplePlayer} to play sounds.
 */
var MusicPlayer = /** @class */ (function () {
    function MusicPlayer(bpm) {
        if (bpm === void 0) { bpm = DEFAULT_BPM; }
        this.bpm = bpm;
        this.samplePlayer = new SamplePlayer();
        this.library = null;
    }
    /**
     * Initializes the MusicPlayer and {@link SamplePlayer} with the music library.
     * Playback cannot start until the player is initialized.
     */
    MusicPlayer.prototype.initialize = function (library) {
        this.library = library;
        this.samplePlayer.initialize(library);
    };
    /**
     * Load a sound into the sound system using the given buffer. Currently only
     * used to upload custom sounds (hidden/demo-only feature).
     *
     * @param id
     * @param buffer
     */
    MusicPlayer.prototype.loadSoundFromBuffer = function (id, buffer) {
        soundApi.LoadSoundFromBuffer(id, buffer);
    };
    MusicPlayer.prototype.getBPM = function () {
        return this.bpm;
    };
    /**
     * Preview the given sound. Plays immediately.
     *
     * @param id unique ID of the sound
     * @param onStop called when the sound finished playing
     */
    MusicPlayer.prototype.previewSound = function (id, onStop) {
        this.samplePlayer.previewSample(id, onStop);
    };
    MusicPlayer.prototype.previewChord = function (chordValue, onStop) {
        var chordEvent = {
            type: 'chord',
            when: 1,
            value: chordValue,
            triggered: false,
            length: constants.DEFAULT_CHORD_LENGTH,
            id: 'preview',
            blockId: 'preview',
        };
        this.samplePlayer.previewSamples(this.convertEventToSamples(chordEvent), onStop);
    };
    MusicPlayer.prototype.previewNote = function (note, instrument, onStop) {
        var sampleId = this.getSampleForNote(note, instrument);
        if (sampleId === null) {
            return;
        }
        this.previewSound(sampleId, onStop);
    };
    MusicPlayer.prototype.previewPattern = function (patternValue, onStop) {
        var patternEvent = {
            type: 'pattern',
            when: 1,
            value: patternValue,
            triggered: false,
            length: constants.DEFAULT_PATTERN_LENGTH,
            id: 'preview',
            blockId: 'preview',
        };
        this.samplePlayer.previewSamples(this.convertEventToSamples(patternEvent), onStop);
    };
    /**
     * Cancels any ongoing previews.
     */
    MusicPlayer.prototype.cancelPreviews = function () {
        this.samplePlayer.cancelPreviews();
    };
    /**
     * Start playback. Schedules all queued playback events for playback
     * and tells the {@link SamplePlayer} to start playing.
     */
    MusicPlayer.prototype.playSong = function (events) {
        var _this = this;
        this.samplePlayer.startPlayback(events.map(function (event) { return _this.convertEventToSamples(event); }).flat());
    };
    /**
     * Play the given events. Assumes that playback is in progress.
     */
    MusicPlayer.prototype.playEvents = function (events) {
        var _this = this;
        this.samplePlayer.playSamples(events.map(function (event) { return _this.convertEventToSamples(event); }).flat());
    };
    /**
     * Stop playback. Tells the {@link SamplePlayer} to stop all sample playback.
     */
    MusicPlayer.prototype.stopSong = function () {
        this.samplePlayer.stopPlayback();
    };
    /**
     * Stop any sounds that have not yet been played if playback is in progress.
     */
    MusicPlayer.prototype.stopAllSoundsStillToPlay = function () {
        this.samplePlayer.stopAllSamplesStillToPlay();
    };
    // Returns the current playhead position, in floating point for an exact position,
    // 1-based, and scaled to measures.
    // Returns 0 if music is not playing.
    MusicPlayer.prototype.getCurrentPlayheadPosition = function () {
        var elapsedTime = this.samplePlayer.getElapsedPlaybackTimeSeconds();
        if (elapsedTime === -1) {
            return 0;
        }
        return this.convertSecondsToPlayheadPosition(elapsedTime);
    };
    // Converts actual seconds used by the audio system into a playhead
    // position, which is 1-based and scaled to measures.
    MusicPlayer.prototype.convertSecondsToPlayheadPosition = function (seconds) {
        return 1 + seconds / this.secondsPerMeasure();
    };
    // Converts a playhead position, which is 1-based and scaled to measures,
    // into actual seconds used by the audio system.
    MusicPlayer.prototype.convertPlayheadPositionToSeconds = function (playheadPosition) {
        return this.secondsPerMeasure() * (playheadPosition - 1);
    };
    MusicPlayer.prototype.secondsPerMeasure = function () {
        return (60 / this.bpm) * BEATS_PER_MEASURE;
    };
    MusicPlayer.prototype.convertEventToSamples = function (event) {
        var _a;
        if ((_a = event.skipContext) === null || _a === void 0 ? void 0 : _a.skipSound) {
            return [];
        }
        if (event.type === 'sound') {
            var soundEvent = event;
            return [
                {
                    sampleId: soundEvent.id,
                    offsetSeconds: this.convertPlayheadPositionToSeconds(soundEvent.when),
                    triggered: soundEvent.triggered,
                    effects: soundEvent.effects,
                },
            ];
        }
        else if (event.type === 'pattern') {
            var patternEvent = event;
            var results = [];
            var kit = patternEvent.value.kit;
            for (var _i = 0, _b = patternEvent.value.events; _i < _b.length; _i++) {
                var event_1 = _b[_i];
                var resultEvent = {
                    sampleId: "".concat(kit, "/").concat(event_1.src),
                    offsetSeconds: this.convertPlayheadPositionToSeconds(patternEvent.when + (event_1.tick - 1) / 16),
                    triggered: patternEvent.triggered,
                    effects: patternEvent.effects,
                };
                results.push(resultEvent);
            }
            return results;
        }
        else if (event.type === 'chord') {
            var results = this.convertChordEventToSampleEvents(event);
            return results;
        }
        return [];
    };
    MusicPlayer.prototype.convertChordEventToSampleEvents = function (event) {
        var _this = this;
        var chordEvent = event;
        var _a = chordEvent.value, instrument = _a.instrument, notes = _a.notes;
        if (notes.length === 0) {
            return [];
        }
        var results = [];
        var generatedNotes = generateNotesFromChord(chordEvent.value);
        generatedNotes.forEach(function (note) {
            var sampleId = _this.getSampleForNote(note.note, instrument);
            if (sampleId === null) {
                console.warn("No sound for note value ".concat(note, " on instrument ").concat(instrument));
            }
            else {
                var noteWhen = chordEvent.when + (note.tick - 1) / 16;
                results.push(__assign({ sampleId: sampleId, offsetSeconds: _this.convertPlayheadPositionToSeconds(noteWhen) }, event));
            }
        });
        return results;
    };
    MusicPlayer.prototype.getSampleForNote = function (note, instrument) {
        if (this.library === null) {
            console.warn('Music Player not initialized');
            return null;
        }
        var folder = this.library.getFolderForPath(instrument);
        if (folder === null) {
            console.warn("No instrument ".concat(instrument));
            return null;
        }
        var sound = folder.sounds.find(function (sound) { return sound.note === note; }) || null;
        if (sound === null) {
            console.warn("No sound for note value ".concat(note, " on instrument ").concat(instrument));
            return null;
        }
        return "".concat(instrument, "/").concat(sound.src);
    };
    return MusicPlayer;
}());
export default MusicPlayer;
